import {
  IMeasureMetadata,
  MeasureDataType,
  MeasureFormatter,
  MeasureIcon,
} from '@principle-theorem/principle-core/interfaces';
import {
  FilterAccessor,
  IComputedAttribute,
  IGroupedDimension,
  IMeasureRef,
  IReportingQuery,
  LabelAccessor,
} from '@principle-theorem/reporting/interfaces';
import {
  AtLeast,
  DAY_MONTH_YEAR_FORMAT,
  ISO_DATE_TIME_FORMAT,
  SHORT_DATE_TIME_FORMAT,
  TIME_FORMAT,
} from '@principle-theorem/shared';
import crossfilter from 'crossfilter2';
import { get, isBoolean, isString, last, startCase } from 'lodash';
import { BigQuerySQL } from '../big-query-sql';
import {
  QueryOptionResolver,
  UNDEFINED_VALUE,
} from '../query-option-resolvers';
import { objectInArrayFilterSQL } from '../querying';
import {
  DataAccessorFactory,
  getProperty,
  MeasurePath,
} from './data-accessor-factory';
import { BooleanMeasureFilter, NullMeasureFilter } from './measure-filters';
import {
  basicFilterAccessor,
  booleanPropertyFilterAccessor,
  CanBeChartedProperty,
  CanDoAllProperty,
  CanQueryByTimestampProperty,
  noFilterAccessor,
  propertyExistsFilterAccessor,
} from './measure-properties';

export function defaultMetadata(
  metadata: AtLeast<IMeasureMetadata, 'id' | 'label' | 'dataType'>
): IMeasureMetadata {
  return {
    summary: '',
    isFilterable: true,
    ...metadata,
  };
}

export class MeasurePropertyFactory {
  static boolean(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label'>,
    measureRef: IMeasureRef,
    measureQuery: IReportingQuery,
    canBeUndefined: boolean = false,
    undefinedIsTrue: boolean = false
  ): CanDoAllProperty {
    const undefinedLabel = 'N/A';
    const dataAccessor = DataAccessorFactory.property<string | boolean>(
      measureRef.factPropertyPath,
      canBeUndefined ? undefinedLabel : undefinedIsTrue,
      undefined,
      (value) => !!value
    );

    const withUndefinedFilterAccessor: FilterAccessor = (filter) =>
      BigQuerySQL.or(
        filter.values.map((filterValue) => {
          if (canBeUndefined && filterValue.value === undefinedLabel) {
            return new NullMeasureFilter(true).value({
              propertyName: measureRef.factPropertyPath,
            });
          }
          return new BooleanMeasureFilter(
            filterValue.value ? true : false
          ).value({
            propertyName: measureRef.factPropertyPath,
          });
        })
      );

    const castUndefinedFilterAccessor = booleanPropertyFilterAccessor(
      measureRef.factPropertyPath,
      undefinedIsTrue
    );

    return new CanDoAllProperty({
      metadata: defaultMetadata({
        dataType: MeasureDataType.Boolean,
        formatter: MeasureFormatter.Boolean,
        optionResolver: QueryOptionResolver.boolean(),
        ...metadata,
      }),
      measure: {
        propertyName: measureRef.attributePath,
        query: measureQuery,
        dataAccessor,
        filterAccessor: canBeUndefined
          ? withUndefinedFilterAccessor
          : castUndefinedFilterAccessor,
      },
      groupMeasure: {
        queryAttributes: [measureRef.attributePath],
        dataAccessor,
        labelAccessor: (fact) => {
          const value = dataAccessor(fact);
          return isBoolean(value) ? (value ? 'Yes' : 'No') : undefinedLabel;
        },
      },
    });
  }

  static computedBoolean(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label'>,
    measureRef: IMeasureRef,
    computedAttribute: IComputedAttribute,
    measureQuery: IReportingQuery
  ): CanDoAllProperty {
    const queryWithComputed: IReportingQuery = {
      ...measureQuery,
      computedAttributes: [
        ...(measureQuery.computedAttributes ?? []),
        computedAttribute,
      ],
    };

    const dataAccessor = DataAccessorFactory.property<string | boolean>(
      measureRef.factPropertyPath,
      false,
      undefined,
      (value) => !!value
    );

    return new CanDoAllProperty({
      metadata: defaultMetadata({
        dataType: MeasureDataType.Boolean,
        formatter: MeasureFormatter.Boolean,
        optionResolver: QueryOptionResolver.boolean(),
        ...metadata,
      }),
      measure: {
        propertyName: measureRef.attributePath,
        query: queryWithComputed,
        dataAccessor,
        filterAccessor: booleanPropertyFilterAccessor(
          measureRef.factPropertyPath
        ),
      },
      groupMeasure: {
        queryAttributes: [],
        dataAccessor,
        labelAccessor: (fact) => {
          const value = dataAccessor(fact);
          return isBoolean(value) ? (value ? 'Yes' : 'No') : 'N/A';
        },
      },
    });
  }

  static propertyExists(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label'>,
    measureRef: IMeasureRef,
    measureQuery: IReportingQuery,
    filterAccessor?: FilterAccessor
  ): CanDoAllProperty {
    const dataAccessor = DataAccessorFactory.property<string | boolean>(
      measureRef.factPropertyPath,
      false,
      undefined,
      (value) => !!value
    );

    return new CanDoAllProperty({
      metadata: defaultMetadata({
        dataType: MeasureDataType.Boolean,
        formatter: MeasureFormatter.Boolean,
        optionResolver: QueryOptionResolver.boolean(),
        ...metadata,
      }),
      measure: {
        propertyName: measureRef.attributePath,
        query: measureQuery,
        dataAccessor,
        filterAccessor:
          filterAccessor ??
          propertyExistsFilterAccessor(measureRef.factPropertyPath),
      },
      groupMeasure: {
        queryAttributes: [measureRef.attributePath],
        dataAccessor,
        labelAccessor: (fact) => (dataAccessor(fact) ? 'Yes' : 'No'),
      },
    });
  }

  static timestamp(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label'>,
    measureRef: IMeasureRef,
    measureQuery: IReportingQuery,
    formatter:
      | MeasureFormatter.Day
      | MeasureFormatter.Timestamp
      | MeasureFormatter.Time = MeasureFormatter.Timestamp,
    sortTimeFormat: string = ISO_DATE_TIME_FORMAT
  ): CanQueryByTimestampProperty {
    const labelFormatter =
      formatter === MeasureFormatter.Day
        ? DAY_MONTH_YEAR_FORMAT
        : formatter === MeasureFormatter.Time
          ? TIME_FORMAT
          : SHORT_DATE_TIME_FORMAT;

    const dataAccessor = DataAccessorFactory.timestamp(
      measureRef.factPropertyPath,
      false
    );
    const labelAccessor = DataAccessorFactory.timestamp(
      measureRef.factPropertyPath,
      'N/A',
      labelFormatter
    );

    return new CanQueryByTimestampProperty({
      queryBy: measureRef,
      metadata: defaultMetadata({
        dataType: MeasureDataType.Date,
        formatter,
        ...metadata,
      }),
      measure: {
        propertyName: measureRef.attributePath,
        query: measureQuery,
        dataAccessor,
        sortAccessor: DataAccessorFactory.timestamp(
          measureRef.factPropertyPath,
          sortTimeFormat
        ),
        filterAccessor: basicFilterAccessor(measureRef.factPropertyPath),
      },
      groupMeasure: {
        queryAttributes: measureQuery.attributes,
        dataAccessor,
        labelAccessor,
      },
    });
  }

  static number(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label'>,
    measure: IMeasureRef,
    measureQuery: IReportingQuery,
    defaultValue: number = 0,
    postProcessFn?: (value: number) => number
  ): CanDoAllProperty {
    const dataPostProcessFn: ((value: string | number) => number) | undefined =
      postProcessFn
        ? (value) =>
            isString(value)
              ? postProcessFn(parseFloat(value))
              : postProcessFn(value)
        : undefined;
    return new CanDoAllProperty({
      metadata: defaultMetadata({
        dataType: MeasureDataType.Number,
        formatter: MeasureFormatter.Number,
        ...metadata,
      }),
      measure: {
        propertyName: measure.attributePath,
        query: measureQuery,
        dataAccessor: DataAccessorFactory.property<number>(
          measure.factPropertyPath,
          defaultValue,
          undefined,
          dataPostProcessFn
        ),
        filterAccessor: basicFilterAccessor(measure.factPropertyPath),
      },
      groupMeasure: {
        queryAttributes: [measure.attributePath],
        dataAccessor: DataAccessorFactory.property<number>(
          measure.factPropertyPath,
          defaultValue,
          undefined,
          dataPostProcessFn
        ),
        labelAccessor: DataAccessorFactory.property<string>(
          measure.factPropertyPath,
          defaultValue.toString(),
          undefined,
          postProcessFn
            ? (value) => postProcessFn(parseFloat(value)).toString()
            : undefined
        ),
      },
    });
  }

  static string(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label'>,
    measure: IMeasureRef,
    measureQuery: IReportingQuery,
    defaultValue: string = '',
    dataAccessorFn?: (data: unknown) => string
  ): CanDoAllProperty {
    return new CanDoAllProperty({
      metadata: defaultMetadata({
        dataType: MeasureDataType.String,
        ...metadata,
      }),
      measure: {
        propertyName: measure.attributePath,
        query: measureQuery,
        dataAccessor: DataAccessorFactory.property<string>(
          measure.factPropertyPath,
          defaultValue,
          dataAccessorFn
        ),
        filterAccessor: basicFilterAccessor(measure.factPropertyPath),
      },
      groupMeasure: {
        queryAttributes: [measure.attributePath],
        dataAccessor: DataAccessorFactory.property<string>(
          measure.factPropertyPath,
          defaultValue,
          dataAccessorFn
        ),
        labelAccessor: DataAccessorFactory.property<string>(
          measure.factPropertyPath,
          defaultValue,
          dataAccessorFn
        ),
      },
    });
  }

  static enum<T extends string>(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label'>,
    measure: IMeasureRef,
    measureQuery: IReportingQuery,
    defaultValue: T | string
  ): CanDoAllProperty {
    return new CanDoAllProperty({
      metadata: defaultMetadata({
        dataType: MeasureDataType.String,
        ...metadata,
      }),
      measure: {
        propertyName: measure.attributePath,
        query: measureQuery,
        dataAccessor: DataAccessorFactory.property<string>(
          measure.factPropertyPath,
          defaultValue,
          undefined,
          (value) => {
            if (value === defaultValue) {
              return UNDEFINED_VALUE;
            }
            return value;
          }
        ),
        labelAccessor: DataAccessorFactory.property<string>(
          measure.factPropertyPath,
          defaultValue,
          undefined,
          (value) => {
            if (value === defaultValue) {
              return defaultValue;
            }
            return startCase(value);
          }
        ),
        filterAccessor: basicFilterAccessor(measure.factPropertyPath),
      },
      groupMeasure: {
        queryAttributes: [measure.attributePath],
        dataAccessor: DataAccessorFactory.property<string>(
          measure.factPropertyPath,
          defaultValue
        ),
        labelAccessor: DataAccessorFactory.property<string>(
          measure.factPropertyPath,
          defaultValue,
          undefined,
          startCase
        ),
      },
    });
  }

  static docRef(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label'>,
    docRef: IMeasureRef,
    measureQuery: IReportingQuery,
    nameMeasure?: IMeasureRef,
    defaultValue: string = ''
  ): CanDoAllProperty {
    const name = nameMeasure ?? docRef;
    const hasOptionResolver = !!metadata.optionResolver;
    const filterByMeasure = hasOptionResolver ? docRef : name;

    const labelAccessor = DataAccessorFactory.property<string>(
      name.factPropertyPath,
      defaultValue,
      undefined,
      (value) =>
        isString(value)
          ? (last(value.split('/')) ?? defaultValue)
          : defaultValue
    );
    const dataAccessor = DataAccessorFactory.property<string>(
      docRef.factPropertyPath,
      defaultValue
    );

    return new CanDoAllProperty({
      metadata: defaultMetadata({
        dataType: MeasureDataType.String,
        icon: MeasureIcon.Referral,
        ...metadata,
      }),
      measure: {
        propertyName: name.attributePath,
        query: measureQuery,
        dataAccessor,
        labelAccessor,
        sortAccessor: labelAccessor,
        filterAccessor: basicFilterAccessor(
          filterByMeasure.factPropertyPath,
          undefined
        ),
      },
      groupMeasure: {
        queryAttributes: [name.attributePath, docRef.attributePath],
        dataAccessor,
        labelAccessor,
      },
    });
  }

  static label(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label'>,
    dataMeasure: IMeasureRef,
    labelMeasure: IMeasureRef,
    measureQuery: IReportingQuery,
    defaultValue: string = ''
  ): CanDoAllProperty {
    const name = labelMeasure ?? dataMeasure;
    return new CanDoAllProperty({
      metadata: defaultMetadata({
        dataType: MeasureDataType.String,
        ...metadata,
      }),
      measure: {
        propertyName: name.attributePath,
        query: measureQuery,
        dataAccessor: DataAccessorFactory.property<string>(
          name.factPropertyPath,
          defaultValue
        ),
        filterAccessor: basicFilterAccessor(
          dataMeasure.factPropertyPath,
          undefined
        ),
      },
      groupMeasure: {
        queryAttributes: [name.attributePath, dataMeasure.attributePath],
        dataAccessor: DataAccessorFactory.property<string>(
          dataMeasure.factPropertyPath,
          defaultValue
        ),
        labelAccessor: DataAccessorFactory.property<string>(
          name.factPropertyPath,
          defaultValue
        ),
      },
    });
  }

  static array<T, R>(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label' | 'dataType'>,
    measure: IMeasureRef,
    measureQuery: IReportingQuery,
    unnestKey: string,
    mapFn?: (fact: T[]) => R[]
  ): CanDoAllProperty {
    const dataAccessor = DataAccessorFactory.array<T, R>(
      measure.factPropertyPath,
      (item) => get(item, unnestKey) as R,
      mapFn
    );
    return new CanDoAllProperty({
      metadata: defaultMetadata(metadata),
      measure: {
        propertyName: measure.attributePath,
        query: measureQuery,
        dataAccessor,
        filterAccessor: (filter, context) =>
          objectInArrayFilterSQL(
            [{ path: measure.factPropertyPath, alias: 'item' }],
            `item.${unnestKey}`,
            filter,
            context
          ),
      },
      groupMeasure: {
        queryAttributes: [measure.attributePath],
        dataAccessor: DataAccessorFactory.array<T, R>(
          measure.factPropertyPath,
          (item) => get(item, unnestKey) as R,
          mapFn
        ),
        arrayDataAccessor: (fact: unknown) =>
          dataAccessor(fact) as unknown as IGroupedDimension[],
      },
    });
  }

  static arrayRef<T, R>(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label' | 'dataType'>,
    measure: IMeasureRef,
    measureQuery: IReportingQuery,
    namePath: string = 'name',
    refPath: string = MeasurePath.docRef('ref')
  ): CanDoAllProperty {
    const dataAccessor = DataAccessorFactory.array<T, R>(
      measure.factPropertyPath,
      (item) => get(item, refPath) as R
    );
    const filterAccessor: FilterAccessor = (filter, context) =>
      objectInArrayFilterSQL(
        [{ path: measure.factPropertyPath, alias: 'item' }],
        `item.${refPath}`,
        filter,
        context
      );
    const labelAccessor = DataAccessorFactory.array<T, R>(
      measure.factPropertyPath,
      (item) => get(item, namePath) as R
    ) as LabelAccessor;

    return new CanDoAllProperty({
      metadata: defaultMetadata(metadata),
      measure: {
        propertyName: measure.attributePath,
        query: measureQuery,
        dataAccessor,
        labelAccessor,
        filterAccessor,
      },
      groupMeasure: {
        queryAttributes: [measure.attributePath],
        dataAccessor,
        labelAccessor,
        arrayDataAccessor: DataAccessorFactory.arrayData(
          measure.factPropertyPath,
          (item) => get(item, refPath) as crossfilter.ComparableValue,
          (item) => get(item, namePath) as string
        ),
      },
    });
  }

  static link(
    metadata: AtLeast<IMeasureMetadata, 'id' | 'label'>,
    refPathMeasure: IMeasureRef,
    measureQuery: IReportingQuery,
    brandSlugMeasure: IMeasureRef,
    getLinkFn: (slug: string, refPath: string) => string | undefined
  ): CanBeChartedProperty {
    return new CanBeChartedProperty({
      metadata: defaultMetadata({
        dataType: MeasureDataType.String,
        formatter: MeasureFormatter.Link,
        isFilterable: false,
        ...metadata,
      }),
      measure: {
        propertyName: refPathMeasure.attributePath,
        query: measureQuery,
        dataAccessor: (fact: unknown) => {
          const slug = getProperty<string>(
            fact,
            brandSlugMeasure.factPropertyPath,
            ''
          );
          const refPath = getProperty<string>(
            fact,
            refPathMeasure.factPropertyPath,
            ''
          );
          if (!slug || !refPath) {
            // eslint-disable-next-line no-console
            console.warn('Invalid invoice link', { slug, refPath });
          }
          return getLinkFn(slug, refPath) ?? '';
        },
        filterAccessor: noFilterAccessor(),
      },
    });
  }
}
